import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
	CategoryFetchResponse,
	CategoryGeneratorState,
	filterCategories,
	getCategoryQuestionTotal,
	SubcategoryGeneratorState,
} from '@quizzach/interfaces';
import {
	checkAvailableQuestions,
	consoleDev,
	ConsoleStatus,
	fetchData,
	readGeneratorSession,
	reorderData,
	syncSessionStorageWithApi,
	validateQuestionCount,
} from '@quizzach/utils';

export const MIN_QUESTIONS = 20;
export const MAX_QUESTIONS = 50;

export type ConfiguratorMode = 'category' | 'subcategory';

const ALL_LEVELS = [1, 2, 3];

const cloneCategory = (
	category: CategoryGeneratorState
): CategoryGeneratorState => ({
	...category,
	levels: [...ALL_LEVELS],
	question_count: null,
	mode: 'category',
	subcategories: category.subcategories.map((sub) => ({
		...sub,
		levels: [...ALL_LEVELS],
		question_count: null,
	})),
});

// default for a newly selected (or re-entered) category-mode category:
// 5 questions, clamped to the category's available pool
const presetQuestionCount = (category: CategoryGeneratorState) =>
	Math.max(0, Math.min(checkAvailableQuestions(category, MAX_QUESTIONS), 5));

// the "unpicked" shape a subcategory returns to (remove, step-to-zero,
// mode switch) — one definition so the invariant can't drift between them
const resetSubcategory = (sub: SubcategoryGeneratorState) => {
	sub.question_count = null;
	sub.levels = [...ALL_LEVELS];
};

const revalidate = (category: CategoryGeneratorState) => {
	const catCheck = validateQuestionCount(
		category,
		category.question_count,
		MAX_QUESTIONS
	);
	category.maximum = catCheck.available;
	category.invalid = catCheck.invalid;
	category.subcategories.forEach((sub) => {
		const subCheck = validateQuestionCount(
			sub,
			sub.question_count ?? null,
			MAX_QUESTIONS
		);
		sub.maximum = subCheck.available;
		sub.invalid = subCheck.invalid;
	});
};

/**
 * Single-page konfigurator state. Same data model, sessionStorage key
 * ('generatorQuiz', CategoryGeneratorState[]) and cart contract as the old
 * two-step flow; only the interaction model is new:
 * - categories start unselected (no preselect-all)
 * - per-category mode: 'category' (tezina chips + one stepper) or
 *   'subcategory' (one list, add prefills all levels + 1 question)
 * - count exclusivity preserved: setting a category count nulls its
 *   subcategory counts and vice versa (backend reads either).
 */
export const useConfigurator = () => {
	const [fetched, setFetched] = useState<CategoryGeneratorState[]>([]);
	const [selected, setSelected] = useState<CategoryGeneratorState[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(false);
	const repeatTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
	const repeatInterval = useRef<ReturnType<typeof setInterval> | null>(null);

	const persist = (next: CategoryGeneratorState[]) => {
		sessionStorage.setItem('generatorQuiz', JSON.stringify(next));
	};

	// The mode is part of the persisted category data (category.mode), so a
	// restore is lossless — an empty subcategory-mode pick survives reloads
	// and the login round-trip instead of being unticked. This map is just a
	// render-friendly view of it.
	const modes = useMemo(() => {
		const map: Record<number, ConfiguratorMode> = {};
		selected.forEach((category) => {
			map[category.id] = category.mode ?? 'category';
		});
		return map;
	}, [selected]);

	const initializeFromSession = useCallback(
		(apiCategories: CategoryGeneratorState[]) => {
			// readGeneratorSession discards corrupt or wrong-shaped storage
			const sessionState = readGeneratorSession();
			if (sessionState.length > 0) {
				const synced = syncSessionStorageWithApi(apiCategories, sessionState)
					.filter((category) =>
						category.subcategories.some((sub) => sub.available_questions > 0)
					)
					// a restored category contributing no questions (empty
					// subcategory-mode pick, or one whose picked subs were
					// drained server-side) acts as unselected — drop it
					.filter((category) => getCategoryQuestionTotal(category) > 0);
				// sessions written before the mode field was persisted — derive
				// it from the data (lossy only for empty selections)
				synced.forEach((category) => {
					if (category.mode) return;
					const hasSubConfig = category.subcategories.some(
						(sub) =>
							(sub.question_count ?? 0) > 0 ||
							(sub.levels && sub.levels.length !== ALL_LEVELS.length)
					);
					category.mode =
						hasSubConfig && !category.question_count
							? 'subcategory'
							: 'category';
				});
				synced.forEach(revalidate);
				setSelected(synced);
				persist(synced);
			} else {
				setSelected([]);
			}
		},
		[]
	);

	/**
	 * Fetches the category list (alphabetical, 'razno'/'ostalo' last) and
	 * returns it ordered, or null on failure. Used on mount and again after a
	 * 409 so validation always runs against current availability.
	 */
	const loadCategories = useCallback(async (): Promise<
		CategoryGeneratorState[] | null
	> => {
		try {
			const response = await fetchData<CategoryFetchResponse[]>(
				'/api/categories',
				{ method: 'GET' },
				{ authRoute: true }
			);
			if (response.status >= 300 || !response.data) {
				setError(true);
				return null;
			}
			const mapped = filterCategories(
				response.data.map(
					(category) => new CategoryGeneratorState(category as any)
				)
			);
			const ordered = reorderData(mapped);
			setError(false);
			setFetched(ordered);
			return ordered;
		} catch (err) {
			consoleDev('configuratorFetch', ConsoleStatus.NET, err);
			setError(true);
			return null;
		}
	}, []);

	useEffect(() => {
		const fetchCategories = async () => {
			try {
				const ordered = await loadCategories();
				if (ordered) initializeFromSession(ordered);
			} catch (err) {
				// a throw here (e.g. stored data the shape guard let through)
				// must not strand the page on skeletons — drop the session and
				// show the fetch-error state instead
				consoleDev('configuratorInit', ConsoleStatus.NET, err);
				sessionStorage.removeItem('generatorQuiz');
				setSelected([]);
				setError(true);
			} finally {
				setLoading(false);
			}
		};
		fetchCategories();
	}, [loadCategories, initializeFromSession]);

	const update = (
		mutate: (draft: CategoryGeneratorState[]) => CategoryGeneratorState[]
	) => {
		setSelected((prev) => {
			const next = mutate(JSON.parse(JSON.stringify(prev)));
			next.forEach(revalidate);
			persist(next);
			return next;
		});
	};

	const isSelected = (categoryId: number) =>
		selected.some((category) => category.id === categoryId);

	const getSelected = (categoryId: number) =>
		selected.find((category) => category.id === categoryId);

	const toggleCategory = (categoryId: number) => {
		if (isSelected(categoryId)) {
			update((draft) => draft.filter((category) => category.id !== categoryId));
		} else {
			const source = fetched.find((category) => category.id === categoryId);
			if (!source) return;
			update((draft) => {
				// same preset as selectAll: 5 questions, clamped to the pool
				const next = cloneCategory(source);
				next.question_count = presetQuestionCount(next);
				return [...draft, next];
			});
		}
	};

	const selectAll = () => {
		const allSelected = fetched.every((category) => isSelected(category.id));
		if (allSelected) {
			update(() => []);
		} else {
			// preset, not just selection: every category lands in category
			// mode with all difficulties and five questions (clamped to the
			// category's available pool) - previous per-category tweaks are
			// replaced so the result is uniform
			update(() =>
				fetched.map((category) => {
					const next = cloneCategory(category);
					next.question_count = presetQuestionCount(next);
					return next;
				})
			);
		}
	};

	// Mode switch resets the other side's counts so the displayed total always
	// matches what would be submitted (same reset the old flow performed).
	const setMode = (categoryId: number, mode: ConfiguratorMode) => {
		// same-mode click must be a no-op: the reset below would wipe the
		// user's counts (and re-preset the category count to 5)
		if ((getSelected(categoryId)?.mode ?? 'category') === mode) return;
		update((draft) => {
			const category = draft.find((c) => c.id === categoryId);
			if (!category) return draft;
			category.mode = mode;
			if (mode === 'category') {
				category.subcategories.forEach(resetSubcategory);
				category.question_count = presetQuestionCount(category);
			} else {
				category.question_count = null;
				category.levels = [...ALL_LEVELS];
			}
			return draft;
		});
	};

	// Category-level difficulty applies to all subcategory levels
	// (the backend only reads subcategory levels).
	const toggleCategoryLevel = (categoryId: number, level: number) => {
		update((draft) => {
			const category = draft.find((c) => c.id === categoryId);
			if (!category) return draft;
			category.levels = category.levels.includes(level)
				? category.levels.filter((l) => l !== level)
				: [...category.levels, level];
			category.subcategories.forEach((sub) => {
				sub.levels = [...category.levels];
			});
			return draft;
		});
	};

	// Subcategory-level difficulty resets the category levels to all
	// (mixed sub levels are no longer representable at category level).
	const toggleSubLevel = (
		categoryId: number,
		subcategoryId: number,
		level: number
	) => {
		update((draft) => {
			const category = draft.find((c) => c.id === categoryId);
			const sub = category?.subcategories.find((s) => s.id === subcategoryId);
			if (!category || !sub) return draft;
			const levels = sub.levels ?? [];
			sub.levels = levels.includes(level)
				? levels.filter((l) => l !== level)
				: [...levels, level];
			category.levels = [...ALL_LEVELS];
			return draft;
		});
	};

	// Counts are computed inside the update() draft (not from the render-time
	// state) so the hold-to-repeat interval, whose closure goes stale after the
	// first tick, still advances one step per tick.
	const setCategoryCount = (
		categoryId: number,
		count: number | ((current: number) => number)
	) => {
		update((draft) => {
			const category = draft.find((c) => c.id === categoryId);
			if (!category) return draft;
			const max = checkAvailableQuestions(category, MAX_QUESTIONS);
			const next =
				typeof count === 'function'
					? count(category.question_count ?? 0)
					: count;
			category.question_count = Math.max(0, Math.min(max, next));
			// exclusivity: category count clears subcategory counts
			category.subcategories.forEach((sub) => {
				sub.question_count = null;
			});
			return draft;
		});
	};

	const setSubCount = (
		categoryId: number,
		subcategoryId: number,
		count: number | ((current: number) => number)
	) => {
		update((draft) => {
			const category = draft.find((c) => c.id === categoryId);
			const sub = category?.subcategories.find((s) => s.id === subcategoryId);
			if (!category || !sub) return draft;
			const max = checkAvailableQuestions(sub, MAX_QUESTIONS);
			const next =
				typeof count === 'function' ? count(sub.question_count ?? 0) : count;
			const clamped = Math.max(0, Math.min(max, next));
			if (clamped === 0) {
				// stepping down to zero deselects the subcategory again
				resetSubcategory(sub);
			} else {
				sub.question_count = clamped;
			}
			// exclusivity: subcategory counts clear the category count
			category.question_count = null;
			return draft;
		});
	};

	const stepCategoryCount = (categoryId: number, delta: number) => {
		setCategoryCount(categoryId, (current) => current + delta);
	};

	const stepSubCount = (
		categoryId: number,
		subcategoryId: number,
		delta: number
	) => {
		setSubCount(categoryId, subcategoryId, (current) => current + delta);
	};

	/** Add prefills all difficulty levels and 1 question. */
	const addSub = (categoryId: number, subcategoryId: number) => {
		update((draft) => {
			const category = draft.find((c) => c.id === categoryId);
			const sub = category?.subcategories.find((s) => s.id === subcategoryId);
			if (!category || !sub) return draft;
			sub.levels = [...ALL_LEVELS];
			if (!sub.question_count) sub.question_count = 1;
			category.question_count = null;
			return draft;
		});
	};

	const removeSub = (categoryId: number, subcategoryId: number) => {
		update((draft) => {
			const category = draft.find((c) => c.id === categoryId);
			const sub = category?.subcategories.find((s) => s.id === subcategoryId);
			if (!category || !sub) return draft;
			resetSubcategory(sub);
			return draft;
		});
	};

	// Press-and-hold repeat for steppers (pointer events unify mouse + touch;
	// pointercancel stops the repeat when touch scrolling takes over).
	const stopRepeat = useCallback(() => {
		if (repeatTimeout.current) clearTimeout(repeatTimeout.current);
		if (repeatInterval.current) clearInterval(repeatInterval.current);
		repeatTimeout.current = null;
		repeatInterval.current = null;
	}, []);

	const startRepeat = (step: () => void) => {
		step();
		stopRepeat();
		repeatTimeout.current = setTimeout(() => {
			repeatInterval.current = setInterval(step, 150);
		}, 300);
		// The pressed button can re-render away mid-press, losing its own
		// pointerup; stop on any release signal at the window level.
		const stopOnce = () => {
			stopRepeat();
			window.removeEventListener('pointerup', stopOnce);
			window.removeEventListener('pointercancel', stopOnce);
			window.removeEventListener('blur', stopOnce);
		};
		window.addEventListener('pointerup', stopOnce);
		window.addEventListener('pointercancel', stopOnce);
		window.addEventListener('blur', stopOnce);
	};

	useEffect(() => stopRepeat, [stopRepeat]);

	const categoryTotal = getCategoryQuestionTotal;

	const total = useMemo(
		() => selected.reduce((sum, category) => sum + categoryTotal(category), 0),
		[selected]
	);

	const hasInvalidLevels = useMemo(
		() =>
			selected.some(
				(category) =>
					category.levels.length === 0 &&
					category.subcategories.every((sub) => (sub.levels ?? []).length === 0)
			) ||
			selected.some((category) =>
				category.subcategories.some(
					(sub) =>
						(sub.question_count ?? 0) > 0 && (sub.levels ?? []).length === 0
				)
			),
		[selected]
	);

	const hasInvalidCounts = useMemo(
		() =>
			selected.some(
				(category) =>
					category.invalid || category.subcategories.some((sub) => sub.invalid)
			),
		[selected]
	);

	// a category-mode selection with zero questions blocks submission (the user
	// explicitly set a count of 0); an empty subcategory-mode category is
	// treated as not selected at all — it shows no error, contributes nothing
	// and is dropped from the payload by transformCategoryStateToCartRequest
	const hasEmptyCategories = useMemo(
		() =>
			selected.some(
				(category) =>
					categoryTotal(category) === 0 &&
					(category.mode ?? 'category') !== 'subcategory'
			),
		[selected]
	);

	const allSelected =
		fetched.length > 0 && fetched.every((category) => isSelected(category.id));

	const canContinue =
		total >= MIN_QUESTIONS &&
		total <= MAX_QUESTIONS &&
		!hasInvalidLevels &&
		!hasInvalidCounts &&
		!hasEmptyCategories;

	/**
	 * After a 409 the server's availability moved on from what we fetched at
	 * mount: re-fetch categories and rebuild the selection from sessionStorage
	 * against the fresh numbers, so the inline max-errors point at the real
	 * problem instead of validating against stale pools.
	 */
	const reinitializeFromSession = async () => {
		const ordered = await loadCategories();
		initializeFromSession(ordered ?? fetched);
	};

	return {
		fetched,
		selected,
		modes,
		loading,
		error,
		total,
		allSelected,
		canContinue,
		hasInvalidLevels,
		hasInvalidCounts,
		hasEmptyCategories,
		isSelected,
		getSelected,
		categoryTotal,
		toggleCategory,
		selectAll,
		setMode,
		toggleCategoryLevel,
		toggleSubLevel,
		setCategoryCount,
		setSubCount,
		stepCategoryCount,
		stepSubCount,
		addSub,
		removeSub,
		startRepeat,
		stopRepeat,
		reinitializeFromSession,
	};
};
